import { forwardRef, type SelectHTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

export type SelectOption = { value: string; label: string };

export type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label?: string;
  error?: string;
  options: SelectOption[];
  placeholder?: string;
};

/**
 * Select — uses the same tonal-layered look as Input. Native <select> for
 * accessibility + simplicity; styled to match the brand. For complex pickers
 * (search, multi) we'll add a Combobox later.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, label, error, options, placeholder, id, ...props }, ref) => {
    const selectId = id ?? `sel-${Math.random().toString(36).slice(2, 9)}`;
    return (
      <label htmlFor={selectId} className="block">
        {label && (
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-label text-on-surface-variant">
            {label}
          </span>
        )}
        <span
          className={cn(
            'flex h-12 items-center rounded-button bg-surface-container-lowest px-4 shadow-soft transition focus-within:shadow-ambient',
            error && 'ring-2 ring-error',
            className,
          )}
        >
          <select
            id={selectId}
            ref={ref}
            className="w-full appearance-none bg-transparent text-on-surface focus:outline-none"
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <span className="material-symbols-outlined pointer-events-none text-on-surface-variant">
            expand_more
          </span>
        </span>
        {error && <span className="mt-1.5 block text-xs text-error">{error}</span>}
      </label>
    );
  },
);

Select.displayName = 'Select';
